// frontend/src/components/SupplierInvoices/SupplierInvoicesView.jsx
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, RefreshCw, FileText } from 'lucide-react';
import SupplierInvoiceStats   from './SupplierInvoiceStats';
import SupplierInvoiceFilters from './SupplierInvoiceFilters';
import SupplierInvoiceTable   from './SupplierInvoiceTable';
import SupplierInvoiceModal   from '../modal/SupplierInvoiceModal';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const SupplierInvoicesView = () => {
  const [invoices,   setInvoices]   = useState([]);
  const [stats,      setStats]      = useState({});
  const [suppliers,  setSuppliers]  = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [modalOpen,  setModalOpen]  = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  const [filters, setFilters] = useState({
    search:       '',
    status:       '',
    invoice_type: '',
    supplier_id:  '',
    start_date:   '',
    end_date:     '',
  });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.search)       params.search       = filters.search;
      if (filters.status)       params.status       = filters.status;
      if (filters.invoice_type) params.invoice_type = filters.invoice_type;
      if (filters.supplier_id)  params.supplier_id  = filters.supplier_id;
      if (filters.start_date)   params.start_date   = filters.start_date;
      if (filters.end_date)     params.end_date     = filters.end_date;

      const [invRes, statsRes, suppRes] = await Promise.all([
        axios.get(`${API_BASE}/supplier-invoices/`,      { params }),
        axios.get(`${API_BASE}/supplier-invoices/stats`),
        axios.get(`${API_BASE}/suppliers/?status=active&limit=500`),
      ]);
      setInvoices(invRes.data   || []);
      setStats(statsRes.data    || {});
      setSuppliers(suppRes.data || []);
    } catch (err) {
      console.error('Error fetching supplier invoices:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleEdit = (inv) => {
    setEditTarget(inv);
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this supplier invoice? This action cannot be undone.')) return;
    try {
      await axios.delete(`${API_BASE}/supplier-invoices/${id}`);
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error deleting invoice');
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await axios.put(`${API_BASE}/supplier-invoices/${id}/status`, { status: newStatus });
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error updating status');
    }
  };

  const handleModalClose = (refreshNeeded) => {
    setModalOpen(false);
    setEditTarget(null);
    if (refreshNeeded) fetchAll();
  };

  return (
    <div className="space-y-4 sm:space-y-6 px-2 sm:px-0">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <FileText size={20} className="text-indigo-400" />
            Supplier Invoices
          </h2>
          <p className="text-gray-400 text-sm mt-0.5">
            Accounts payable — vendor bills and recurring charges
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchAll}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-700/50 rounded-lg transition-colors"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={() => { setEditTarget(null); setModalOpen(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-lg text-white text-sm font-medium transition-colors"
          >
            <Plus size={16} />
            New Invoice
          </button>
        </div>
      </div>

      <SupplierInvoiceStats stats={stats} />

      <SupplierInvoiceFilters
        filters={filters}
        setFilters={setFilters}
        suppliers={suppliers}
      />

      <SupplierInvoiceTable
        invoices={invoices}
        loading={loading}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onStatusChange={handleStatusChange}
      />

      {modalOpen && (
        <SupplierInvoiceModal
          invoice={editTarget}
          suppliers={suppliers}
          onClose={handleModalClose}
        />
      )}
    </div>
  );
};

export default SupplierInvoicesView;